import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";

// Info-dot (design Turn 8): a discreet ⓘ beside a page title. Desktop → a 280px
// popover with a little arrow + "Got it" link; mobile → a bottom sheet with a grab
// handle + full-width "Got it" button. The glyph is a Georgia-serif italic "i" in
// an 18px #E8F1FF / #1B6FE0 circle. First view opens it once (onboarding); after
// "Got it" the dot stays but only opens on click (persisted in localStorage).

const STORAGE_PREFIX = "scoutInfoDot:ack:";
// Per-session guard so the onboarding auto-open fires once, not on every re-render
// or route revisit within a session.
const autoShownThisSession = new Set();

const isAcked = (id) => {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === "1";
  } catch (e) {
    return false;
  }
};

// `variant="lm"` renders the Last Minute handoff optics (LM design 7c/8k):
// closed dot = grey-bordered Georgia-serif "i", open dot = solid #2283FF + soft
// ring; popover = 340px card with a filled "Got it" button; mobile sheet per 8k.
// Default variant is byte-identical to the original Scout rendering.
const ScoutInfoDot = ({ id, title, body, className = "", variant = "scout" }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Onboarding: auto-open once when not yet acknowledged.
  useEffect(() => {
    if (!isAcked(id) && !autoShownThisSession.has(id)) {
      autoShownThisSession.add(id);
      setOpen(true);
    }
  }, [id]);

  // Desktop: close on outside click.
  useEffect(() => {
    if (!open || isMobile) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, isMobile]);

  const gotIt = () => {
    try {
      localStorage.setItem(STORAGE_PREFIX + id, "1");
    } catch (e) {
      /* storage unavailable — still close */
    }
    setOpen(false);
  };

  const lm = variant === "lm";

  const Dot = (
    <button
      type="button"
      aria-label={`What is this: ${title}`}
      onClick={() => setOpen((v) => !v)}
      className={`inline-flex items-center justify-center w-[18px] h-[18px] rounded-full align-middle flex-shrink-0 ${
        lm ? "" : "bg-[#E8F1FF] text-[#1B6FE0]"
      }`}
      style={{
        fontFamily: "Georgia, serif",
        fontStyle: "italic",
        fontSize: lm ? "11px" : "12px",
        fontWeight: lm ? 700 : undefined,
        lineHeight: 1,
        ...(lm
          ? open
            ? { background: "#2283FF", color: "#fff", boxShadow: "0 0 0 3px rgba(34,131,255,.18)" }
            : { border: "1.4px solid #C3CFDF", color: "#8A98AC" }
          : {}),
      }}
    >
      i
    </button>
  );

  return (
    <span ref={wrapRef} className={`relative inline-flex ${className}`}>
      {Dot}

      {/* Desktop popover */}
      {open && !isMobile && (
        <span
          role="dialog"
          className={
            lm
              ? "absolute left-0 top-[26px] z-50 w-[340px] max-w-[80vw] bg-white border border-[#E4E9F1] rounded-[14px] p-[18px] text-left shadow-[0_20px_44px_-12px_rgba(16,24,40,0.25)] flex flex-col gap-2.5"
              : "absolute left-0 top-[26px] z-50 w-[280px] bg-white border border-[#E5E7EB] rounded-[10px] p-3.5 text-left shadow-[0_8px_24px_rgba(17,24,39,0.12)]"
          }
        >
          <span
            className={`absolute -top-1.5 left-2 w-3 h-3 bg-white border-l border-t rotate-45 ${
              lm ? "border-[#E4E9F1]" : "border-[#E5E7EB]"
            }`}
            aria-hidden="true"
          />
          <span className={`block font-bold ${lm ? "text-[14px] text-[#14263D]" : "text-[13.5px] text-[#111827]"}`}>
            {title}
          </span>
          <span
            className={`block ${lm ? "text-[12.5px] text-[#5A6B82] leading-[1.6]" : "text-[12.5px] text-[#374151] leading-snug mt-1"}`}
          >
            {body}
          </span>
          {lm ? (
            <button
              type="button"
              onClick={gotIt}
              className="self-end rounded-[9px] bg-[#2283FF] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#1B6FE0]"
            >
              Got it
            </button>
          ) : (
            <button
              type="button"
              onClick={gotIt}
              className="mt-2.5 text-[12.5px] font-semibold text-[#2283FF] hover:underline"
            >
              Got it
            </button>
          )}
        </span>
      )}

      {/* Mobile bottom sheet */}
      {open && isMobile &&
        createPortal(
          <div className="fixed inset-0 z-[1000] flex items-end" role="dialog">
            <div
              className={`absolute inset-0 ${lm ? "bg-[rgba(16,26,40,0.5)]" : "bg-[rgba(17,24,39,0.35)]"}`}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            {lm ? (
              // LM 8k sheet: 22px radius, grab handle, active dot + title row,
              // full-width filled "Got it".
              <div className="relative w-full bg-white rounded-t-[22px] px-5 pt-2.5 pb-10 flex flex-col gap-3.5">
                <span className="mx-auto w-[38px] h-[4.5px] rounded-full bg-[#D5DDE8]" />
                <div className="flex items-center gap-2.5">
                  <span
                    className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2283FF] text-white font-bold"
                    style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "12px" }}
                  >
                    i
                  </span>
                  <p className="text-[17px] font-bold text-[#14263D]">{title}</p>
                </div>
                <p className="text-[13px] text-[#5A6B82] leading-[1.65]">{body}</p>
                <button
                  type="button"
                  onClick={gotIt}
                  className="w-full rounded-xl bg-[#2283FF] text-white text-[14px] font-semibold py-3.5 text-center"
                >
                  Got it
                </button>
              </div>
            ) : (
              <div className="relative w-full bg-white rounded-t-2xl p-5 pb-7 shadow-[0_-8px_30px_rgba(17,24,39,0.18)]">
                <div className="mx-auto mb-4 w-9 h-1 rounded-full bg-[#E5E7EB]" />
                <span
                  className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#E8F1FF] text-[#1B6FE0] mb-2"
                  style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: "14px" }}
                >
                  i
                </span>
                <p className="text-[15px] font-bold text-[#111827]">{title}</p>
                <p className="text-[13px] text-[#374151] leading-snug mt-1.5">{body}</p>
                <button
                  type="button"
                  onClick={gotIt}
                  className="mt-4 w-full rounded-lg bg-[#2283FF] text-white text-[14px] font-semibold py-3"
                >
                  Got it
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </span>
  );
};

export default ScoutInfoDot;
